import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX, FiShield, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import "./PrivacyPolicyModal.css";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: (
      <>
        <p>We may collect the following information:</p>
        <ul>
          <li>Full Name</li>
          <li>Mobile Number</li>
          <li>Email Address</li>
          <li>Service Address</li>
          <li>City and State</li>
          <li>Booking Details</li>
          <li>
            Payment Information (processed securely through third-party payment
            providers)
          </li>
          <li>
            Device Information (IP address, browser type, operating system)
          </li>
          <li>Website usage information through cookies and analytics</li>
        </ul>
      </>
    ),
  },
  {
    title: "2. How We Use Your Information",
    body: (
      <>
        <p>We use your information to:</p>
        <ul>
          <li>Process and manage service bookings</li>
          <li>Contact you regarding appointments and updates</li>
          <li>Provide customer support</li>
          <li>Improve our website and services</li>
          <li>Send promotional offers (only if you choose to receive them)</li>
          <li>Prevent fraud and maintain platform security</li>
          <li>Comply with applicable legal requirements</li>
        </ul>
      </>
    ),
  },
  {
    title: "3. Cookies",
    body: (
      <p>
        Our website uses cookies to improve user experience, remember your
        preferences, analyze website traffic, and enhance website performance.
        You may disable cookies through your browser settings, although some
        website features may not function properly.
      </p>
    ),
  },
  {
    title: "4. Sharing of Information",
    body: (
      <>
        <p>We do not sell, rent, or trade your personal information.</p>
        <p>We may share limited information only with:</p>
        <ul>
          <li>Verified service professionals assigned to your booking</li>
          <li>Trusted payment processing partners</li>
          <li>Technology providers supporting our platform</li>
          <li>Government authorities when legally required</li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Data Security",
    body: (
      <p>
        We implement appropriate technical and organizational security measures
        to protect your personal information against unauthorized access,
        misuse, alteration, or disclosure. While we strive to protect your data,
        no online system can guarantee complete security.
      </p>
    ),
  },
  {
    title: "6. Third-Party Services",
    body: (
      <p>
        Our website may contain links to third-party websites or services. We
        are not responsible for the privacy practices or content of those
        external websites. Users are encouraged to review their privacy policies
        separately.
      </p>
    ),
  },
  {
    title: "7. Your Rights",
    body: (
      <>
        <p>You may request to:</p>
        <ul>
          <li>Access your personal information</li>
          <li>Correct inaccurate information</li>
          <li>Update your contact details</li>
          <li>
            Delete your personal information (subject to legal obligations)
          </li>
          <li>Withdraw marketing communications at any time</li>
        </ul>
        <p>To make a request, please contact us using the details below.</p>
      </>
    ),
  },
  {
    title: "8. Children's Privacy",
    body: (
      <p>
        Our services are intended for users aged 18 years or older. We do not
        knowingly collect personal information from children.
      </p>
    ),
  },
  {
    title: "9. Changes to This Privacy Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page along with the updated effective date.
      </p>
    ),
  },
];

const PrivacyPolicyModal = ({ open, onClose, variant = "click" }) => {
  const cardRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Lock scroll + focus the close button + restore focus to trigger on close
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = originalOverflow;
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  // Escape to close, basic focus trap
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && cardRef.current) {
        const focusable = cardRef.current.querySelectorAll(
          'button, a[href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={`modal-overlay privacy-modal-overlay${
        variant === "hover" ? " is-hover-preview" : ""
      }`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card privacy-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-modal-title"
        ref={cardRef}
      >
        <div className="modal-header privacy-modal-header">
          <h3 id="privacy-modal-title">
            <FiShield size={16} className="privacy-modal-title-icon" />
            Privacy Policy
          </h3>
          <button
            type="button"
            className="modal-close"
            aria-label="Close privacy policy"
            onClick={onClose}
            ref={closeBtnRef}
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="privacy-modal-body">
          <p className="privacy-modal-updated">Last Updated: June 2026</p>
          <p className="privacy-modal-intro">
            At Expert At Door, we value your privacy and are committed to
            protecting your personal information. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your information when
            you visit{" "}
            <a
              href="https://www.Expert At Door.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.Expert At Door.com
            </a>{" "}
            or use our services.
          </p>

          {SECTIONS.map((section) => (
            <section className="privacy-modal-section" key={section.title}>
              <h4>{section.title}</h4>
              {section.body}
            </section>
          ))}

          <section className="privacy-modal-section privacy-modal-contact">
            <h4>10. Contact Us</h4>
            <p>
              If you have any questions regarding this Privacy Policy or your
              personal information, please contact us:
            </p>
            <p className="privacy-modal-company">Expert At Door</p>
            <ul className="privacy-modal-contact-list">
              <li>
                <FiMapPin size={14} />
                <span>Kolkata, West Bengal, India</span>
              </li>
              <li>
                <FiMail size={14} />
                <a href="mailto:Expert At Door@gmail.com">
                  Expert At Door@gmail.com
                </a>
              </li>
              <li>
                <FiPhone size={14} />
                <a href="tel:+918981002675">+91 89810 02675</a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PrivacyPolicyModal;
